/**
 * Germination test arithmetic: seedtrac's QC handheld Germination Setup and Evaluation (decks
 * `Germination_Setup.pptx`, `Germination Evaluation.pptx`) and the web's QC Data Verification grid.
 *
 * Pure functions shared by the API, which stores the counts, and both clients, which show the means
 * live while the analyst counts, so a percentage can never be worked out two different ways.
 *
 *   SGT  Standard Germination Test, on paper in the lab: per replication Normal Type-A, Normal
 *        Type-B, Abnormal, Dead, Hard and FUG (fresh ungerminated) seeds.
 *   FGT  Field Germination Test, in a medium (soil ...): Normal Type-A, Normal Type-B, Abnormal, Dead.
 *   RE   Radicle Emergence (user, 2026-10-22), counted exactly like the SGT.
 *
 * The Lab Manager sets the tray up on the web (any of the three methods, each with its replications,
 * seeds and whether a First Count is taken); the analyst only counts on the handheld.
 *
 * Seedlings are counted twice. At the First Count the normal seedlings are counted and removed; at the
 * Final Count everything left is counted. A replication's figure is the two counts added together,
 * and a mean is that total over every replication as a % of the seeds set: the deck's two SGT
 * replications of 50 seeds with 49 and 47 normal Type-A read "Mean of Normal Type-A 96".
 */
import { z } from "zod";
import { formatDate } from "./dates";

/**
 * The legacy single-value setup, kept on every tray for APKs installed before the web setup: `both`
 * runs an SGT and an FGT on the same sample. A tray's `methods` list is the truth.
 */
export const GERM_SETUP_METHODS = ["sgt", "fgt", "both"] as const;
export type GermSetupMethod = (typeof GERM_SETUP_METHODS)[number];

export const GERM_SETUP_METHOD_LABELS: Record<GermSetupMethod, string> = {
  sgt: "SGT",
  fgt: "FGT",
  both: "SGT and FGT",
};

/** One of the tests a setup can run, and the one a verification settles on. */
export const GERM_METHODS = ["sgt", "fgt", "re", "dbt", "cold", "aat"] as const;
export type GermMethod = (typeof GERM_METHODS)[number];
export const germMethod = z.enum(GERM_METHODS);

/**
 * Radicle Emergence is written in full (user, 2026-10-22); only table cells use the short form, with
 * the full name on hover (`GERM_METHOD_SHORT`).
 */
export const GERM_METHOD_LABELS: Record<GermMethod, string> = {
  sgt: "SGT",
  fgt: "FGT",
  re: "Radicle Emergence",
  dbt: "DBT",
  cold: "Cold Test",
  aat: "AAT",
};

/** The short form for table cells, where the full name shows on hover. */
export const GERM_METHOD_SHORT: Record<GermMethod, string> = {
  sgt: "SGT",
  fgt: "FGT",
  re: "RE",
  dbt: "DBT",
  cold: "Cold Test",
  aat: "AAT",
};

/**
 * Each test's full name, the hover on its short form. DBT, Cold Test and AAT (user, 2026-10-31) run
 * beside the SGT, counted like it, on paper; DBT and AAT show short, Cold Test in full.
 */
export const GERM_METHOD_FULL: Record<GermMethod, string> = {
  sgt: "Standard Germination Test",
  fgt: "Field Germination Test",
  re: "Radicle Emergence",
  dbt: "Dormancy Breaking Treatment",
  cold: "Cold Test",
  aat: "Accelerated Aging Test",
};

/** The setup form's names; the same as the labels since RE is written in full. */
export const GERM_METHOD_NAMES: Record<GermMethod, string> = {
  sgt: "SGT",
  fgt: "FGT",
  re: "Radicle Emergence",
  dbt: "DBT",
  cold: "Cold Test",
  aat: "AAT",
};

/**
 * The methods a setup runs, in the order the screens show them: from the tray's `methods` list, or
 * from the legacy single value of a tray (or an API) that predates it.
 */
export function germMethodsOf(
  setup: readonly string[] | GermSetupMethod | null | undefined
): GermMethod[] {
  if (!setup) return [];
  if (typeof setup === "string")
    return setup === "both" ? ["sgt", "fgt"] : [setup];
  return GERM_METHODS.filter((m) => setup.includes(m));
}

/** "SGT + FGT + Radicle Emergence". */
export function germMethodsText(methods: readonly GermMethod[]): string {
  return methods.map((m) => GERM_METHOD_NAMES[m]).join(" + ");
}

/** The legacy single value for a methods list: its SGT/FGT part, null for an RE-only tray. */
export function germLegacyMethod(
  methods: readonly GermMethod[]
): GermSetupMethod | null {
  const sgt = methods.includes("sgt");
  const fgt = methods.includes("fgt");
  return sgt && fgt ? "both" : sgt ? "sgt" : fgt ? "fgt" : null;
}

/** Hard and FUG seeds are counted on every test but the FGT. */
export const germHasHardFug = (m: GermMethod): boolean => m !== "fgt";

/** The tests set on paper (Top of or Between the paper): the SGT, DBT, Cold Test and AAT. */
export const GERM_PAPER_METHODS = ["sgt", "dbt", "cold", "aat"] as const;
export type GermPaperMethod = (typeof GERM_PAPER_METHODS)[number];
export const germPaperMedia = (m: GermMethod): m is GermPaperMethod =>
  (GERM_PAPER_METHODS as readonly string[]).includes(m);

export const GERM_OBSERVATIONS = ["first", "final"] as const;
export type GermObservation = (typeof GERM_OBSERVATIONS)[number];
export const germObservation = z.enum(GERM_OBSERVATIONS);

export const GERM_OBSERVATION_LABELS: Record<GermObservation, string> = {
  first: "First Count",
  final: "Final Count",
};

/**
 * The FGT's Media Type. The legacy dropdown showed Soil; the rest are the usual lab media. Paper was
 * dropped on 2026-10-22 (the SGT is the paper test); older trays keep it, so its label stays.
 */
export const GERM_MEDIA = ["soil", "sand", "cocopeat"] as const;
export type GermMedium = (typeof GERM_MEDIA)[number];
export const GERM_MEDIA_LABELS: Record<GermMedium | "paper", string> = {
  soil: "Soil",
  sand: "Sand",
  cocopeat: "Cocopeat",
  paper: "Paper",
};

/** The SGT's Media Type (user, 2026-10-22). */
export const GERM_SGT_MEDIA = ["top_paper", "between_paper"] as const;
export type GermSgtMedium = (typeof GERM_SGT_MEDIA)[number];
export const GERM_SGT_MEDIA_LABELS: Record<GermSgtMedium, string> = {
  top_paper: "Top of the paper",
  between_paper: "Between the paper",
};

/** Any stored medium in words, an unknown code as itself. */
export function germMediumLabel(
  code: string | null | undefined
): string | null {
  if (!code) return null;
  return (
    (GERM_MEDIA_LABELS as Record<string, string>)[code] ??
    (GERM_SGT_MEDIA_LABELS as Record<string, string>)[code] ??
    code
  );
}

/** Size of Seeds, from the crop: the deck's "Big". */
export const GERM_SEED_SIZES = ["big", "small"] as const;
export type GermSeedSize = (typeof GERM_SEED_SIZES)[number];
export const GERM_SEED_SIZE_LABELS: Record<GermSeedSize, string> = {
  big: "Big",
  small: "Small",
};

/** Vigour remark. */
export const GERM_VIGOUR = ["good", "average", "poor"] as const;
export type GermVigour = (typeof GERM_VIGOUR)[number];
export const GERM_VIGOUR_LABELS: Record<GermVigour, string> = {
  good: "Good",
  average: "Average",
  poor: "Poor",
};

/** Technician Remark ticks, kept as the legacy codes. */
export const GERM_TECH_REMARKS = ["rssc", "prm", "vss"] as const;
export type GermTechRemark = (typeof GERM_TECH_REMARKS)[number];
export const GERM_TECH_REMARK_LABELS: Record<GermTechRemark, string> = {
  rssc: "RSSC",
  prm: "PRM",
  vss: "VSS",
};

/** Replications a setup may ask for. The legacy dropdowns went to 4; a little headroom. */
export const GERM_MAX_REPS = 8;
/** Seeds in one replication. */
export const GERM_MAX_SEEDS_PER_REP = 400;

/** Where a sample's germination test has got to. */
export const GERM_STATUSES = [
  "not_applicable",
  "not_set_up",
  "in_test",
  "submitted",
  "verified",
] as const;
export type GermStatus = (typeof GERM_STATUSES)[number];

export const GERM_STATUS_LABELS: Record<GermStatus, string> = {
  not_applicable: "Not asked",
  not_set_up: "Waiting for setup",
  in_test: "In test",
  submitted: "Submitted",
  verified: "Verified",
};

/** One replication's counts at one observation. Hard and FUG on every test but the FGT. */
export interface GermRepCounts {
  normalA: number;
  normalB: number;
  abnormal: number;
  dead: number;
  hard?: number | null;
  fug?: number | null;
}

const count = z.coerce.number().int().min(0).max(GERM_MAX_SEEDS_PER_REP);
export const germRepCounts = z.object({
  normalA: count,
  normalB: count,
  abnormal: count,
  dead: count,
  hard: count.nullable().optional(),
  fug: count.nullable().optional(),
});

/** Seeds a replication's counts account for. */
export function germRepTotal(c: GermRepCounts): number {
  return (
    c.normalA + c.normalB + c.abnormal + c.dead + (c.hard ?? 0) + (c.fug ?? 0)
  );
}

/**
 * One replication's seed accounting. The Final Count is a running total (user, 2026-10-24, as the
 * SeedTrac deck shows: SGT 49 + 1 = 50 of 50): it already holds the seedlings seen at the First
 * Count, so it alone must account for every seed set. The First Count is a progress check that may
 * not exceed the seeds. Every screen and refusal says it the same way, so "50/50" (seeds per
 * replication) is never read as "50 of 50 counted".
 */
export interface GermRepTally {
  /** Seeds the First Count recorded; null when it was not taken. */
  first: number | null;
  /** Seeds the Final Count recorded; null until it is taken. */
  final: number | null;
  /** The figure held against the seeds: the Final Count once taken, else the First Count (0 if neither). */
  total: number;
  /** Seeds set in one replication; null before setup. */
  seeds: number | null;
  /** Seeds the Final Count has yet to account for; 0 when all are, null without a seed count. */
  remaining: number | null;
  /** Seeds counted beyond the replication; 0 normally, null without a seed count. */
  over: number | null;
}

export function germRepTally(
  first: GermRepCounts | null | undefined,
  final: GermRepCounts | null | undefined,
  seeds: number | null | undefined
): GermRepTally {
  const f = first ? germRepTotal(first) : null;
  const l = final ? germRepTotal(final) : null;
  const total = l ?? f ?? 0;
  const set = seeds ?? null;
  return {
    first: f,
    final: l,
    total,
    seeds: set,
    remaining: set === null ? null : Math.max(0, set - total),
    over: set === null ? null : Math.max(0, total - set),
  };
}

/**
 * "Final 48 of 50 seeds · 2 still to count · First Count 20" (or "all counted", "5 too many"); before
 * the Final Count, "First 20 of 50 seeds"; on a method set up without a First Count, "Final 40 of 50
 * seeds · 10 still to count".
 */
export function germRepTallyText(t: GermRepTally, finalOnly = false): string {
  const of = `of ${t.seeds ?? "—"} seeds`;
  const onFirst = !finalOnly && t.final === null && t.first !== null;
  const sum = onFirst
    ? `First ${t.first} ${of}`
    : `Final ${t.final ?? 0} ${of}`;
  const firstNote =
    !finalOnly && !onFirst && t.first !== null
      ? ` · First Count ${t.first}`
      : "";
  if (t.seeds === null) return `${sum}${firstNote}`;
  if (t.over) return `${sum} · ${t.over} too many${firstNote}`;
  if (onFirst) return sum;
  if (t.remaining) return `${sum} · ${t.remaining} still to count${firstNote}`;
  return `${sum} · all counted${firstNote}`;
}

/**
 * Why a count cannot be submitted, every short (or over) replication named at once, so the analyst
 * fixes them all in one pass. Null when every replication is in order. `final` asks the Final Count
 * to account for every seed; a First Count only may not exceed the seeds set.
 */
export function germCountProblem(
  method: string,
  tallies: GermRepTally[],
  final: boolean,
  finalOnly = false
): string | null {
  const bad = tallies
    .map((t, i) => ({ t, rep: i + 1 }))
    .filter(({ t }) => (t.over ?? 0) > 0 || (final && (t.remaining ?? 0) > 0));
  if (!bad.length) return null;
  const list = bad
    .map(
      ({ t, rep }) => `replication ${rep}: ${germRepTallyText(t, finalOnly)}`
    )
    .join("; ");
  const fix = bad.some(({ t }) => (t.over ?? 0) > 0)
    ? "Each replication can hold only the seeds set for it."
    : finalOnly
      ? "The Final Count must account for every seed set; record a lost or rotten seed as Dead."
      : "The Final Count must account for every seed set, the seedlings seen at the First Count included; record a lost or rotten seed as Dead.";
  const name =
    (GERM_METHOD_LABELS as Record<string, string>)[method] ??
    method.toUpperCase();
  return `${name} ${list}. ${fix}`;
}

export interface GermMeans {
  normalA: number;
  normalB: number;
  /** Normal seedlings, Type-A plus Type-B: the germination %. One decimal, as the deck shows 96.0. */
  normal: number;
  abnormal: number;
  dead: number;
  hard: number;
  fug: number;
}

const whole = (n: number) => Math.round(n);
const oneDp = (n: number) => Math.round(n * 10) / 10;

/**
 * The Mean (%) block: each category summed over every replication, as a % of the seeds set. The
 * result is worked out from the Final Count alone (a running total); the First Count alone gives
 * the early hint. Null when nothing was set (no replications or no seeds per replication).
 */
export function germinationMeans(
  reps: GermRepCounts[],
  seedsPerRep: number | null | undefined,
  denominatorReps = reps.length
): GermMeans | null {
  if (!seedsPerRep || denominatorReps === 0 || reps.length === 0) return null;
  const seeds = seedsPerRep * denominatorReps;
  const sum = (k: keyof GermRepCounts) =>
    reps.reduce((n, r) => n + Number(r[k] ?? 0), 0);
  const pct = (k: keyof GermRepCounts) => (sum(k) / seeds) * 100;
  return {
    normalA: whole(pct("normalA")),
    normalB: whole(pct("normalB")),
    normal: oneDp(((sum("normalA") + sum("normalB")) / seeds) * 100),
    abnormal: whole(pct("abnormal")),
    dead: whole(pct("dead")),
    hard: whole(pct("hard")),
    fug: whole(pct("fug")),
  };
}

/**
 * A germination verdict: Borderline is for the QC supervisor to decide at Result Update. Retest
 * (user, 2026-10-31) is the supervisor's pick at verification: the round is set aside and the next
 * round decides.
 */
export const GERMINATION_RESULTS = [
  "pass",
  "borderline",
  "fail",
  "retest",
] as const;
export type GerminationResult = (typeof GERMINATION_RESULTS)[number];
export const GERMINATION_RESULT_LABELS: Record<GerminationResult, string> = {
  pass: "Pass",
  borderline: "Borderline",
  fail: "Fail",
  retest: "Retest",
};

/**
 * The QC Result dropdown on Germination verification (user, 2026-10-31): the verdicts plus Invalid,
 * which opens Mark Invalid instead of verifying.
 */
export const GERM_VERIFY_CHOICES = [...GERMINATION_RESULTS, "invalid"] as const;
export type GermVerifyChoice = (typeof GERM_VERIFY_CHOICES)[number];
export const GERM_VERIFY_CODES: Record<GermVerifyChoice, string> = {
  pass: "OK",
  borderline: "BL",
  fail: "Fail",
  retest: "RT",
  invalid: "Invalid",
};

/**
 * The Germination Test Result against the crop's bands (docs/designs/qc-crop-standards.md): at or
 * above the OK mark passes; below the Fail mark fails; anything between is Borderline. With only an
 * OK mark it is pass/fail as before; with none, null (the verifier picks).
 */
export function germinationResult(
  pct: number,
  okAtPct: number | null | undefined,
  failBelowPct?: number | null
): Exclude<GerminationResult, "retest"> | null {
  if (okAtPct == null) return null;
  if (pct >= okAtPct) return "pass";
  if (failBelowPct == null) return "fail";
  return pct < failBelowPct ? "fail" : "borderline";
}

/**
 * When the germination result will be in (user, 2026-10-17). The lab's sheet gives each crop the day
 * after setup (GKD = day 0) of its First Count and of its Final Count; the Final Count is when the
 * result is known. Moisture and PP come back the same day, so this is what the QC pages show while a
 * tray waits.
 */
export type GermCountState = "upcoming" | "today" | "late" | "done";

export interface GermCountDue {
  /** ISO date the count falls on. */
  on: string;
  state: GermCountState;
  /** Days still to go (upcoming) or days overdue (late); 0 otherwise. */
  days: number;
}

export interface GermSchedule {
  /** Null when the crop has no First Count (Soya Bean on the sheet) or no rule. */
  first: GermCountDue | null;
  /** Null when the crop has no Final Count day yet. */
  final: GermCountDue | null;
}

const DAY_MS = 86_400_000;
const isoDay = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

/** `2026-10-17` plus 4 days → `2026-10-21`. */
export function addDaysIso(iso: string, days: number): string {
  return new Date(isoDay(iso) + days * DAY_MS).toISOString().slice(0, 10);
}

function countDue(
  setupOn: string,
  day: number,
  today: string,
  done: boolean
): GermCountDue {
  const on = addDaysIso(setupOn, day);
  if (done) return { on, state: "done", days: 0 };
  const diff = Math.round((isoDay(on) - isoDay(today)) / DAY_MS);
  if (diff > 0) return { on, state: "upcoming", days: diff };
  if (diff === 0) return { on, state: "today", days: 0 };
  return { on, state: "late", days: -diff };
}

/**
 * Both count days for one tray. A count is done once the lab saved it: any First Count row, and the
 * DOE (stamped by the first Final Count) for the final. Null when the tray is not set up or the crop
 * has no count days.
 */
export function germSchedule(p: {
  setupOn: string | null | undefined;
  firstDay: number | null | undefined;
  finalDay: number | null | undefined;
  today: string;
  firstDone?: boolean | undefined;
  finalDone?: boolean | undefined;
}): GermSchedule | null {
  if (!p.setupOn || (p.firstDay == null && p.finalDay == null)) return null;
  const finalDone = !!p.finalDone;
  return {
    first:
      p.firstDay == null
        ? null
        : countDue(p.setupOn, p.firstDay, p.today, !!p.firstDone || finalDone),
    final:
      p.finalDay == null
        ? null
        : countDue(p.setupOn, p.finalDay, p.today, finalDone),
  };
}

const inDays = (n: number) => (n === 1 ? "tomorrow" : `in ${n} days`);
const daysLate = (n: number) => (n === 1 ? "1 day late" : `${n} days late`);

/** Plain words for one count, for supervisors: "First count on 21/10/2026 (in 3 days)". */
export function germCountWords(
  kind: GermObservation,
  due: GermCountDue
): string {
  const what = kind === "first" ? "First count" : "Result";
  switch (due.state) {
    case "done":
      return kind === "first" ? "First count done" : "Final count done";
    case "today":
      return `${what} is due today`;
    case "late":
      return `${what} is ${daysLate(due.days)} (was due ${formatDate(due.on)})`;
    default:
      return `${what} on ${formatDate(due.on)} (${inDays(due.days)})`;
  }
}

/**
 * How a tray is doing after its First Count, against the crop's OK mark. Only a hint: seedlings still
 * come up before the Final Count, which alone decides the result (user, 2026-10-17).
 */
export function germFirstCountWords(
  pct: number,
  okAtPct: number | null | undefined
): { text: string; good: boolean | null } {
  const p = `${Math.round(pct * 10) / 10}%`;
  if (okAtPct == null) return { text: `${p} germinated so far`, good: null };
  return pct >= okAtPct
    ? {
        text: `${p} germinated — good, already at the ${okAtPct}% needed`,
        good: true,
      }
    : {
        text: `${p} germinated so far — ${okAtPct}% needed, the final count decides`,
        good: false,
      };
}
